import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { BooleanDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { NumberDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { ExpressionRule } from "../../../../../rule-engine-core/Expressions/ExpressionRule";
import { ExpressionValueType } from "../../../../../rule-engine-core/Expressions/ExpressionValueType";
import { GreaterThanExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/GreaterThan";
import { GreaterThanEqualToExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/GreaterThanEqualTo";
import {
    IfThenExpression,
    IfThenPair,
    IfThenStyle,
} from "../../../../../rule-engine-core/Expressions/Expressions/Logic/IfThenExpression";
import { LessThanExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/LessThanExpression";
import { LessThanEqualToExpression } from "../../../../../rule-engine-core/Expressions/Expressions/Logic/LessThanEqualToExpression";
import { StatementRuleConfiguration } from "../../../../../rule-engine-core/Rule/Statement/StatementRuleConfiguration";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import type { RuleOutcomeKey, RuleOutcomeRouting } from "../../../rules/ruleLogicDefinitions";
import { getOutcomeRouting } from "../../../rules/ruleLogicDefinitions";
import type {
    RuleIntegrationEvaluationContext,
    RuleSimplifiedLogicEdit,
    RuleSimplifiedLogicFieldOption,
    RuleSummaryAst,
    RuleSummaryPresentation,
} from "../../ruleIntegrationTypes";
import { createRuleSummaryPresentation } from "../../ruleIntegrationTypes";
import { summarizeIfThenExpression } from "../../appglueExpressionSummary";
import {
    runStatementRuleBoolean,
} from "../../executableFamilies/statementRule/StatementRuleIntegrationAdapter";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";
import type { StatementRuleIntegrationPolicy } from "../statementRulePolicyTypes";

export type NumericThresholdOperator = ">" | ">=" | "<" | "<=";

type NumericThresholdState = {
    field: string;
    operator: NumericThresholdOperator;
    threshold: number;
};

type NumericThresholdCharacterization =
    | {
          status: "complete";
          state: NumericThresholdState;
          warnings: string[];
      }
    | {
          status: "unsupported";
          state: null;
          warnings: string[];
      };

export type NumericThresholdStatementPolicyParams<DemoRow> = {
    ruleId: string;
    title: string;
    conditionId: string;
    defaultField: string;
    defaultOperator: NumericThresholdOperator;
    defaultThreshold: number;
    fieldOptions: RuleSimplifiedLogicFieldOption[];
    valueInputLabel: string;
    comparisonLabel: string;
    summaryActionWhen: RuleOutcomeKey;
    defaultRouting: Record<RuleOutcomeKey, RuleOutcomeRouting>;
    getNumericValue: (row: DemoRow, field: string) => number | null | undefined;
    formatComparisonValue?: (value: number) => string;
    runtimeSamples: {
        passingRow: DemoRow;
        routedRow: DemoRow;
    };
};

type NumericThresholdEvidence = {
    status: DemoRuleStatus;
    field: string;
    operator: NumericThresholdOperator;
    threshold: number;
    actualValue?: number | null;
    appGlueRuntimeError?: string;
    unsupportedNativeExpression?: boolean;
    outputQueue?: DemoOutputQueue;
};

const STATEMENT_RULE_EXECUTABLE_TYPE = "StatementRuleConfiguration";

type NumericComparisonExpression =
    | GreaterThanExpression
    | GreaterThanEqualToExpression
    | LessThanExpression
    | LessThanEqualToExpression;

const unsupportedCharacterization = (message: string): NumericThresholdCharacterization => ({
    status: "unsupported",
    state: null,
    warnings: [message],
});

const createInputDefinition = (fieldOptions: RuleSimplifiedLogicFieldOption[]): DataDefinition => {
    const inputData = new DataDefinition();
    fieldOptions.forEach((field) => {
        inputData.fields.push(new NumberDataDefinition(field.value));
    });
    return inputData;
};

const buildExecutionData = <DemoRow>(
    row: DemoRow,
    params: NumericThresholdStatementPolicyParams<DemoRow>,
): ExecutionData =>
    new ExecutionData(
        Object.fromEntries(
            params.fieldOptions.map((field) => [field.value, params.getNumericValue(row, field.value)]),
        ),
    );

const createNumericComparison = (state: NumericThresholdState): ExpressionRule => {
    const expression = (() => {
        if (state.operator === ">") return new GreaterThanExpression();
        if (state.operator === ">=") return new GreaterThanEqualToExpression();
        if (state.operator === "<") return new LessThanExpression();
        return new LessThanEqualToExpression();
    })();

    expression.value1.variableName = state.field;
    expression.value2.value = state.threshold;
    return expression;
};

const setBranchExpression = (branch: IfThenPair, expression: ExpressionRule) => {
    branch.ifExpression.elements = [];
    branch.ifExpression.add();
    branch.ifExpression.elements[0]!.subExpression = expression;
};

const buildNumericThresholdExpression = (state: NumericThresholdState): IfThenExpression => {
    const expression = new IfThenExpression();
    expression.ifThenStyle = IfThenStyle.BRANCH_DATA_RETURN;

    const passBranch = expression.ifBranches[0]!;
    setBranchExpression(passBranch, createNumericComparison(state));
    passBranch.thenExpression.value = true;
    expression.defaultOutput.value = false;

    return expression;
};

const createConfig = <DemoRow>(params: NumericThresholdStatementPolicyParams<DemoRow>) => {
    AppGlueCoreInit.init();

    const config = new StatementRuleConfiguration();
    config.title = params.title;
    config.inputData = createInputDefinition(params.fieldOptions);
    config.executionResult = new BooleanDataDefinition();
    config.expression.expression = buildNumericThresholdExpression({
        field: params.defaultField,
        operator: params.defaultOperator,
        threshold: params.defaultThreshold,
    });

    return config;
};

const getComparisonStateFromExpression = (
    expression: NumericComparisonExpression,
    operator: NumericThresholdOperator,
): NumericThresholdState | null =>
    typeof expression.value1.variableName === "string" && typeof expression.value2.value === "number"
        ? {
              field: expression.value1.variableName,
              operator,
              threshold: expression.value2.value,
          }
        : null;

const getComparisonState = (expression: ExpressionRule | undefined): NumericThresholdState | null => {
    if (expression instanceof GreaterThanExpression) return getComparisonStateFromExpression(expression, ">");
    if (expression instanceof GreaterThanEqualToExpression) return getComparisonStateFromExpression(expression, ">=");
    if (expression instanceof LessThanExpression) return getComparisonStateFromExpression(expression, "<");
    if (expression instanceof LessThanEqualToExpression) return getComparisonStateFromExpression(expression, "<=");
    return null;
};

const characterizeNumericThreshold = <DemoRow>(
    config: StatementRuleConfiguration,
    params: NumericThresholdStatementPolicyParams<DemoRow>,
): NumericThresholdCharacterization => {
    const expression = config.expression.expression;
    if (!(expression instanceof IfThenExpression)) {
        return unsupportedCharacterization("Numeric-threshold summary supports IfThenExpression statement rules only.");
    }

    if (expression.ifBranches.length !== 1) {
        return unsupportedCharacterization(
            `Numeric-threshold summary expects exactly 1 branch; found ${expression.ifBranches.length}.`,
        );
    }
    if (expression.ifBranches[0]!.thenExpression.value !== true || expression.defaultOutput.value !== false) {
        return unsupportedCharacterization(
            "Numeric-threshold summary supports true-on-threshold, false-default expressions only.",
        );
    }

    const branchExpressions = expression.ifBranches[0]!.ifExpression.elements.map((element) => element.subExpression);
    if (branchExpressions.length !== 1) {
        return unsupportedCharacterization(
            `Numeric-threshold summary expects 1 comparison clause; found ${branchExpressions.length}.`,
        );
    }

    const state = getComparisonState(branchExpressions[0]);
    if (!state) return unsupportedCharacterization("Numeric-threshold summary found an unsupported comparison.");

    const allowedFields = new Set(params.fieldOptions.map((option) => option.value));
    if (!allowedFields.has(state.field)) {
        return unsupportedCharacterization(`Numeric-threshold summary found unsupported field ${state.field}.`);
    }

    return {
        status: "complete",
        state,
        warnings: [],
    };
};

const normalizeWithOptions = (values: string[], options: RuleSimplifiedLogicFieldOption[]): string[] => {
    const optionsByValue = new Map(options.map((option) => [option.value.toLowerCase(), option.value]));
    const optionsByLabel = new Map(options.map((option) => [option.label.toLowerCase(), option.value]));

    return Array.from(
        new Set(
            values
                .map((value) => {
                    const key = value.trim().toLowerCase();
                    return optionsByValue.get(key) ?? optionsByLabel.get(key);
                })
                .filter((value): value is string => !!value),
        ),
    );
};

const formatNumberForSummary = (value: number): string => Number(value.toFixed(4)).toString();

const buildSummaryAst = <DemoRow>(
    state: NumericThresholdState,
    params: NumericThresholdStatementPolicyParams<DemoRow>,
    context?: RuleIntegrationEvaluationContext,
): RuleSummaryAst => ({
    kind: "rule_summary",
    statements: [
        {
            kind: "condition",
            keyword: "IF",
            expression: {
                kind: "comparison",
                left: { kind: "field", field: state.field },
                operator: state.operator,
                right: { kind: "literal", value: state.threshold },
            },
            editPort: {
                conditionId: params.conditionId,
                canEditField: true,
                canEditValues: true,
            },
        },
        {
            kind: "action",
            label: getRouting(params, context, params.summaryActionWhen).label,
        },
    ],
});

const getRouting = <DemoRow>(
    params: NumericThresholdStatementPolicyParams<DemoRow>,
    context: RuleIntegrationEvaluationContext | undefined,
    when: RuleOutcomeKey,
): RuleOutcomeRouting => getOutcomeRouting(context?.definition, when) ?? params.defaultRouting[when];

const defaultState = <DemoRow>(params: NumericThresholdStatementPolicyParams<DemoRow>): NumericThresholdState => ({
    field: params.defaultField,
    operator: params.defaultOperator,
    threshold: params.defaultThreshold,
});

const unsupportedNativeSummary = <DemoRow>(
    config: StatementRuleConfiguration,
    warnings: string[],
    params: NumericThresholdStatementPolicyParams<DemoRow>,
    context?: RuleIntegrationEvaluationContext,
): RuleSummaryPresentation => {
    const expression = config.expression.expression;
    if (expression instanceof IfThenExpression) {
        const nativeSummary = summarizeIfThenExpression(expression);
        return createRuleSummaryPresentation({
            status: "unsupported",
            ast: {
                kind: "rule_summary",
                statements: [
                    ...nativeSummary.ast.statements,
                    {
                        kind: "action",
                        label: getRouting(params, context, params.summaryActionWhen).label,
                    },
                ],
            },
            sources: ["native_appglue_expression", "adapter_routing"],
            warnings: [...warnings, ...nativeSummary.warnings],
        });
    }

    return createRuleSummaryPresentation({
        status: "unsupported",
        lines: ["APPGLUE native expression requires the native editor for full logic"],
        sources: ["native_appglue_expression"],
        warnings,
    });
};

const renderSummary = <DemoRow>(
    config: StatementRuleConfiguration,
    params: NumericThresholdStatementPolicyParams<DemoRow>,
    context?: RuleIntegrationEvaluationContext,
): RuleSummaryPresentation => {
    if (config.expression.valueType === ExpressionValueType.VALUE) {
        return createRuleSummaryPresentation({
            status: "complete",
            lines: [`RESULT ${String(config.expression.value)}`],
            sources: ["native_appglue_expression"],
        });
    }

    const characterization = characterizeNumericThreshold(config, params);
    return characterization.status === "complete"
        ? createRuleSummaryPresentation({
              status: "complete",
              ast: buildSummaryAst(characterization.state, params, context),
              editable: true,
              sources: ["native_appglue_expression", "adapter_routing"],
          })
        : unsupportedNativeSummary(config, characterization.warnings, params, context);
};

const applyEdit = <DemoRow>(
    config: StatementRuleConfiguration,
    edit: RuleSimplifiedLogicEdit,
    params: NumericThresholdStatementPolicyParams<DemoRow>,
): StatementRuleConfiguration => {
    if (edit.conditionId !== params.conditionId) return config;

    const characterization = characterizeNumericThreshold(config, params);
    if (characterization.status !== "complete") return config;

    const currentState = characterization.state;
    const fields = edit.field ? normalizeWithOptions([edit.field], params.fieldOptions) : [currentState.field];
    const threshold = edit.values?.[0] === undefined ? currentState.threshold : Number(edit.values[0]);
    if (fields.length === 0 || !Number.isFinite(threshold)) return config;

    config.expression.expression = buildNumericThresholdExpression({
        field: fields[0]!,
        operator: currentState.operator,
        threshold,
    });
    return config;
};

const formatComparisonValue = <DemoRow>(
    value: number,
    params: NumericThresholdStatementPolicyParams<DemoRow>,
): string => params.formatComparisonValue?.(value) ?? formatNumberForSummary(value);

export const createNumericThresholdStatementRulePolicy = <DemoRow>(
    params: NumericThresholdStatementPolicyParams<DemoRow>,
): StatementRuleIntegrationPolicy<DemoRow> => ({
    ruleId: params.ruleId,
    policyBinding: requireRulePolicyBinding(params.ruleId),
    createConfig: () => createConfig(params),
    evaluator: {
        evaluate: (config, row, context) => {
            const { appGluePassed, appGlueRuntimeError } = runStatementRuleBoolean(
                config,
                buildExecutionData(row, params),
            );
            const characterization = characterizeNumericThreshold(config, params);
            const state = characterization.status === "complete" ? characterization.state : defaultState(params);
            const actualValue = params.getNumericValue(row, state.field);
            const routing = getRouting(params, context, appGlueRuntimeError ? "error" : appGluePassed ? "pass" : "fail");
            const evidence: NumericThresholdEvidence = {
                status: routing.status,
                field: state.field,
                operator: state.operator,
                threshold: state.threshold,
                actualValue,
                appGlueRuntimeError,
                unsupportedNativeExpression: characterization.status !== "complete" ? true : undefined,
                outputQueue: routing.outputQueue,
            };

            return {
                passed: appGluePassed,
                status: evidence.status,
                reasonCode:
                    evidence.unsupportedNativeExpression && !appGlueRuntimeError
                        ? appGluePassed
                            ? "APPGLUE_NATIVE_EXPRESSION_PASSED"
                            : "APPGLUE_NATIVE_EXPRESSION_REJECTED"
                        : routing.reasonCode,
                explanation:
                    evidence.unsupportedNativeExpression && !appGlueRuntimeError
                        ? "The native AppGlue expression evaluated this row, but its shape is outside the simplified numeric-threshold evidence model. Use the native editor for full logic."
                        : routing.explanation,
                outputQueue: routing.outputQueue,
                comparisons: appGlueRuntimeError
                    ? [
                          {
                              label: "AppGlue expression",
                              expected: "complete executable expression",
                              actual: appGlueRuntimeError,
                              ok: false,
                          },
                      ]
                    : evidence.unsupportedNativeExpression
                      ? [
                            {
                                label: "AppGlue expression",
                                expected: "row passes native expression",
                                actual: appGluePassed ? "passed" : "rejected",
                                ok: appGluePassed,
                            },
                        ]
                      : [
                            {
                                label: params.comparisonLabel,
                                expected: `${state.operator} ${formatComparisonValue(state.threshold, params)}`,
                                actual:
                                    actualValue === null || actualValue === undefined
                                        ? "missing"
                                        : formatComparisonValue(actualValue, params),
                                ok: appGluePassed,
                            },
                        ],
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: STATEMENT_RULE_EXECUTABLE_TYPE,
                },
                evidence,
            };
        },
    },
    summaryPresenter: {
        renderSummary: (config, context) => renderSummary(config, params, context),
    },
    simplifiedLogicEditor: {
        getEditorModel: (config) => {
            const characterization = characterizeNumericThreshold(config, params);
            if (characterization.status !== "complete") return null;

            return {
                condition: {
                    id: params.conditionId,
                    keyword: "IF",
                    field: characterization.state.field,
                    fieldOptions: params.fieldOptions,
                    operatorLabel: characterization.state.operator,
                    values: [formatNumberForSummary(characterization.state.threshold)],
                    valueOptions: [],
                    valueInputLabel: params.valueInputLabel,
                },
            };
        },
        applyEdit: (config, edit) => applyEdit(config, edit, params),
    },
    runtimeSamples: params.runtimeSamples,
});
